import React, { useEffect, useState, useCallback } from 'react';
import {
    Paper, Typography, CircularProgress, Box, Tab, Tabs,
    TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
    TextField, Tooltip, IconButton, TablePagination,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import apiService from '../../services/actions';
import { toast } from 'react-toastify';

const T = {
    primary: '#1976d2',
    primaryLight: '#e3f2fd',
    primaryMid: '#bbdefb',
    primaryDark: '#1565c0',
    secondary: '#9c27b0',
    success: '#2e7d32',
    successBg: '#e8f5e9',
    error: '#c62828',
    errorBg: '#ffebee',
    errorMid: '#ef9a9a',
    border: '#e0e0e0',
    rowHover: '#f0f7ff',
    rowAlt: '#fafafa',
    textPrimary: '#212121',
    textSecondary: '#616161',
    sectionBg: '#f8fafc',
    disabled: '#bdbdbd',
};

// yyyy-MM-dd for today
const todayStr = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const formatDateTime = (d) => {
    if (!d) return '-';
    try {
        return new Date(d).toLocaleString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    } catch {
        return d;
    }
};

function ReportFailureList() {
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [logDate, setLogDate] = useState(todayStr());
    
    // Data states
    const [sharingFailures, setSharingFailures] = useState([]);
    const [statusFailures, setStatusFailures] = useState([]);
    
    // Pagination states
    const [sharingPage, setSharingPage] = useState(0);
    const [sharingRowsPerPage, setSharingRowsPerPage] = useState(20);
    const [sharingTotal, setSharingTotal] = useState(0);
    const [sharingTotalPages, setSharingTotalPages] = useState(1);
    
    const [statusPage, setStatusPage] = useState(0);
    const [statusRowsPerPage, setStatusRowsPerPage] = useState(20);
    const [statusTotal, setStatusTotal] = useState(0);
    const [statusTotalPages, setStatusTotalPages] = useState(1);

    // Fetch Whatsapp vendor failures (failed-whatsapp)
    const fetchSharingFailures = useCallback(async (date = logDate, page = sharingPage, size = sharingRowsPerPage) => {
        if (!date) return;
        setLoading(true);
        try {
            const res = await apiService.whatsappVendorFailure({ date, page, size });
            const data = res?.data?.data || res?.data || {};
            const content = data?.content || [];
            setSharingFailures(Array.isArray(content) ? content : []);
            setSharingTotal(data?.page?.totalElements || 0);
            setSharingTotalPages(data?.page?.totalPages || 1);
        } catch (error) {
            console.error('Error fetching Whatsapp vendor failures:', error);
            setSharingFailures([]);
            setSharingTotal(0);
            setSharingTotalPages(1);
            toast.error('Failed to fetch Whatsapp vendor failures');
        }
        setLoading(false);
    }, [logDate, sharingPage, sharingRowsPerPage]);

    // Fetch status failures (pending-reports)
    const fetchStatusFailures = useCallback(async (date = logDate, page = statusPage, size = statusRowsPerPage) => {
        if (!date) return;
        setLoading(true);
        try {
            const res = await apiService.statusFailure({ date, page, size });
            const data = res?.data?.data || res?.data || {};
            const content = data?.content || [];
            setStatusFailures(Array.isArray(content) ? content : []);
            setStatusTotal(data?.page?.totalElements || 0);
            setStatusTotalPages(data?.page?.totalPages || 1);
        } catch (error) {
            console.error('Error fetching status failures:', error);
            setStatusFailures([]);
            setStatusTotal(0);
            setStatusTotalPages(1);
            toast.error('Failed to fetch status failures');
        }
        setLoading(false);
    }, [logDate, statusPage, statusRowsPerPage]);

    // Initial load
    useEffect(() => {
        fetchSharingFailures();
        fetchStatusFailures();
    }, []);

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        // Reset pagination when switching tabs
        if (newValue === 0) {
            setSharingPage(0);
        } else {
            setStatusPage(0);
        }
    };

    // Handle date change
    const handleDateChange = (e) => {
        const v = e.target.value;
        setLogDate(v);
        // Reset pagination on date change
        setSharingPage(0);
        setStatusPage(0);
        fetchSharingFailures(v, 0, sharingRowsPerPage);
        fetchStatusFailures(v, 0, statusRowsPerPage);
    };

    // Handle refresh
    const handleRefresh = () => {
        if (activeTab === 0) {
            fetchSharingFailures(logDate, sharingPage, sharingRowsPerPage);
        } else {
            fetchStatusFailures(logDate, statusPage, statusRowsPerPage);
        }
    };

    // Pagination handlers for Whatsapp vendor failures
    const handleSharingPageChange = (event, newPage) => {
        setSharingPage(newPage);
        fetchSharingFailures(logDate, newPage, sharingRowsPerPage);
    };

    const handleSharingRowsPerPageChange = (event) => {
        const newSize = parseInt(event.target.value, 10);
        setSharingRowsPerPage(newSize);
        setSharingPage(0);
        fetchSharingFailures(logDate, 0, newSize);
    };

    // Pagination handlers for status failures
    const handleStatusPageChange = (event, newPage) => {
        setStatusPage(newPage);
        fetchStatusFailures(logDate, newPage, statusRowsPerPage);
    };

    const handleStatusRowsPerPageChange = (event) => {
        const newSize = parseInt(event.target.value, 10);
        setStatusRowsPerPage(newSize);
        setStatusPage(0);
        fetchStatusFailures(logDate, 0, newSize);
    };

    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            height: 34, borderRadius: '7px', fontSize: '0.8125rem', bgcolor: '#fff',
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.primary },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.primary },
        },
        '& .MuiInputLabel-root': { fontSize: '0.8125rem', top: '-2px', '&.MuiInputLabel-shrink': { top: 0 } },
        '& .MuiInputLabel-root.Mui-focused': { color: T.primary },
    };

    // Render table for either tab
    const renderTable = (data, page, rowsPerPage, total, onPageChange, onRowsPerPageChange, isSharing = true) => {
        return (
            <>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)', minHeight: 350, overflow: 'auto' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow sx={{ background: `linear-gradient(90deg, ${T.primaryLight} 0%, #f8f9ff 100%)` }}>
                                <TableCell sx={{ fontWeight: 700, fontSize: '12px', letterSpacing: '0.04em', textTransform: 'uppercase', color: T.primary, borderBottom: `2px solid ${T.primaryMid}`, py: 1.5, bgcolor: T.primaryLight }}>
                                    Order ID
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '12px', letterSpacing: '0.04em', textTransform: 'uppercase', color: T.primary, borderBottom: `2px solid ${T.primaryMid}`, py: 1.5, bgcolor: T.primaryLight }}>
                                    Booking ID
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={2} align="center" sx={{ py: 6 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, color: T.textSecondary }}>
                                            <ReportProblemOutlinedIcon sx={{ fontSize: 40, opacity: 0.25 }} />
                                            <Typography variant="body2">No failures found</Typography>
                                            <Typography variant="caption">Try a different date</Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                data.map((row, index) => (
                                    <TableRow
                                        key={row.orderId || index}
                                        sx={{
                                            bgcolor: index % 2 === 0 ? '#fff' : T.rowAlt,
                                            '&:hover': { bgcolor: T.rowHover },
                                            '& td': { borderBottom: `1px solid ${T.border}`, py: 1.25, fontSize: '13px' },
                                        }}
                                    >
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: 500 }}>
                                            {row.orderId || '-'}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '13px', color: T.textSecondary }}>
                                            {row.bookingId || '-'}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    component="div"
                    count={total}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={onPageChange}
                    onRowsPerPageChange={onRowsPerPageChange}
                    sx={{
                        borderTop: `1px solid ${T.border}`,
                        '& .MuiTablePagination-select': { fontSize: '13px' },
                        '& .MuiTablePagination-displayedRows': { fontSize: '13px' },
                    }}
                />
            </>
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', mt: 0, px: 0 }}>
            {/* Header */}
            <Box
                sx={{
                    position: 'sticky',
                    top: 64,
                    zIndex: 10,
                    bgcolor: '#fff',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    mb: 1,
                    pt: 0.5,
                    pb: 1,
                    borderBottom: `1px solid ${T.border}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Box sx={{
                        width: 32, height: 32, borderRadius: '8px',
                        background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDark} 100%)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: `0 2px 8px ${T.primaryMid}`,
                    }}>
                        <ReportProblemOutlinedIcon sx={{ color: '#fff', fontSize: 18 }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '15px', color: T.textPrimary, lineHeight: 1.2 }}>Report Failures</Typography>
                        <Typography variant="caption" sx={{ color: T.textSecondary, fontSize: '11px' }}>View Whatsapp vendor and status failures</Typography>
                    </Box>
                </Box>

                {/* Filters + Refresh */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <TextField
                        size="small" label="Date" type="date" variant="outlined"
                        value={logDate}
                        onChange={handleDateChange}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ max: todayStr() }}
                        sx={{ minWidth: 145, ...fieldSx }}
                    />

                    <Tooltip title="Refresh">
                        <IconButton
                            onClick={handleRefresh}
                            disabled={loading}
                            sx={{
                                border: `1px solid ${T.border}`,
                                borderRadius: '7px',
                                width: 34,
                                height: 34,
                                color: T.textSecondary,
                                '&:hover': { borderColor: T.primary, color: T.primary, bgcolor: T.primaryLight },
                            }}
                        >
                            <RefreshIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Loading Overlay */}
            {loading && (
                <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.35)', zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress sx={{ color: T.primary }} />
                </Box>
            )}

            {/* Tabs */}
            <Paper elevation={0} sx={{ borderRadius: '12px', border: `1px solid ${T.border}`, overflow: 'hidden' }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    sx={{
                        borderBottom: `1px solid ${T.border}`,
                        bgcolor: T.sectionBg,
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '14px',
                            py: 1.5,
                            minHeight: 'auto',
                            '&.Mui-selected': {
                                color: T.primary,
                            },
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: T.primary,
                            height: 3,
                        },
                    }}
                >
                    <Tab label={`Whatsapp Vendor Failure (${sharingTotal})`} />
                    <Tab label={`Status Failure (${statusTotal})`} />
                </Tabs>

                {/* Tab Panels */}
                <Box sx={{ p: 2 }}>
                    {activeTab === 0 && (
                        <Box>
                            <Typography variant="caption" sx={{ color: T.textSecondary, display: 'block', mb: 1 }}>
                                <strong>{sharingTotal}</strong> Whatsapp vendor failure{sharingTotal !== 1 ? 's' : ''} found
                            </Typography>
                            {renderTable(
                                sharingFailures,
                                sharingPage,
                                sharingRowsPerPage,
                                sharingTotal,
                                handleSharingPageChange,
                                handleSharingRowsPerPageChange,
                                true
                            )}
                        </Box>
                    )}
                    {activeTab === 1 && (
                        <Box>
                            <Typography variant="caption" sx={{ color: T.textSecondary, display: 'block', mb: 1 }}>
                                <strong>{statusTotal}</strong> status failure{statusTotal !== 1 ? 's' : ''} found
                            </Typography>
                            {renderTable(
                                statusFailures,
                                statusPage,
                                statusRowsPerPage,
                                statusTotal,
                                handleStatusPageChange,
                                handleStatusRowsPerPageChange,
                                false
                            )}
                        </Box>
                    )}
                </Box>
            </Paper>
        </Box>
    );
}

export default ReportFailureList;